import { InfiniteData } from "@tanstack/react-query";
import { IServerSideGetRowsParams, SortModelItem } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { RefObject } from "react";
import { ReviewDataResponse } from "../hooks/useFetchReviewData";
import { PAGE_SIZE } from "../inSeasonReviewConfig";

/**
 * Creates a server-side datasource for AG-Grid with infinite query integration
 */
export function createServerSideDataSource(
  fetchNextPage: () => Promise<{ data?: InfiniteData<ReviewDataResponse> }>,
  currentDataRef: RefObject<ReviewDataResponse>
) {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      const { startRow, endRow } = params.request;
      const availableData = currentDataRef.current || [];

      // Check if grid requesting data that we already have cached
      if (endRow && endRow <= availableData.length) {
        params.success({
          rowData: availableData.slice(startRow ?? 0, endRow)
        });
        return;
      }

      // If not, fetch the next page
      try {
        const { data } = await fetchNextPage();
        const lastPage = data?.pages?.[data.pages.length - 1] ?? [];

        // Check if no more data is returned
        if (!lastPage || lastPage.length === 0) {
          params.success({
            rowData: [],
            rowCount: data?.pages.flatMap((page) => page).length
          });
        } else {
          params.success({
            rowData: lastPage,
            rowCount:
              lastPage.length < PAGE_SIZE
                ? data?.pages.flatMap((page) => page).length
                : undefined
          });
        }
      } catch (error) {
        console.error("Failed to fetch:", error);
        params.fail();
      }
    }
  };
}

/**
 * Extracts sort model from AG-Grid and returns it as SortModelItem array
 */
export function extractSortModelFromGrid(
  gridRef: RefObject<AgGridReact | null>
): SortModelItem[] {
  if (!gridRef.current) {
    return [];
  }

  const columns = gridRef.current.api.getColumnState();

  // Filter columns to keep the one with sorting and arrange the order by sortIndex
  const sortedColumns = columns
    .filter((column) => column.sort !== null && column.sortIndex !== null)
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

  // Build the sort model like in AG-Grid
  const sortModel = sortedColumns.map(
    (column) =>
      ({
        colId: column.colId,
        sort: column.sort
      }) as SortModelItem
  );

  return sortModel;
}
